import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  LuSearch,
  LuPlus,
  LuHistory,
  LuEye,
  LuPencil,
  LuCheck,
  LuX,
  LuUsers,
  LuClock,
} from "react-icons/lu";
import ConnectorLogo from "../connectors/ConnectorLogo";
import { formatRelativeTime, HealthBadge } from "../connectors/helpers";
import {
  fetchConnectors,
  fetchSyncsTodayCount,
  getApiErrorMessage,
  setConnectorEnabled,
  startConnectorSync,
} from "../connectors/integrationsService";

const HEALTH_OPTIONS = ["Healthy", "Warning", "Failed"];

function Toggle({ checked, onChange, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={onChange}
      className="relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors"
      style={{ backgroundColor: checked ? "#528416" : "#D1D5DB" }}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
          checked ? "translate-x-5" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}

function StatItem({ title, value, valueClass = "text-[#031F36]", icon, iconBg }) {
  return (
    <div className="bg-white border border-[#E6E7EB] rounded-lg px-5 py-4 flex items-center justify-between min-w-0 shadow-sm">
      <div className="min-w-0">
        <p className="text-[12px] leading-[14px] text-[#898F99] uppercase">{title}</p>
        <p className={`mt-1 text-2xl font-semibold ${valueClass}`}>{value}</p>
      </div>
      <div
        className="h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: iconBg }}
      >
        {icon}
      </div>
    </div>
  );
}

export default function IntegrationsTab() {
  const navigate = useNavigate();
  const [connectors, setConnectors] = useState([]);
  const [syncsToday, setSyncsToday] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState("");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  const load = useCallback(async () => {
    setError("");
    const listRes = await fetchConnectors({ page: 1, page_size: 100 });
    setConnectors(listRes.items);
    try {
      setSyncsToday(await fetchSyncsTodayCount());
    } catch {
      setSyncsToday(null);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        await load();
      } catch (err) {
        if (!cancelled) {
          setError(getApiErrorMessage(err, "Could not load connectors."));
          setConnectors([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [load]);

  const stats = useMemo(() => {
    const total = connectors.length;
    const healthy = connectors.filter((c) => c.health === "Healthy").length;
    const failed = connectors.filter((c) => c.health === "Failed").length;
    return { total, healthy, failed, syncsToday: syncsToday == null ? "—" : syncsToday };
  }, [connectors, syncsToday]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return connectors.filter((c) => {
      const matchesQuery =
        !q ||
        c.name.toLowerCase().includes(q) ||
        c.type.toLowerCase().includes(q) ||
        String(c.connectionStatus || "").toLowerCase().includes(q);
      const matchesStatus = !statusFilter || c.health === statusFilter;
      const matchesType = !typeFilter || c.type === typeFilter;
      return matchesQuery && matchesStatus && matchesType;
    });
  }, [connectors, query, statusFilter, typeFilter]);

  const openAdd = () => navigate("/settings/add-connector");
  const openView = (connector) => navigate(`/settings/connectors/${connector.id}`);
  const openEdit = (connector) => navigate(`/settings/connectors/${connector.id}?edit=1`);

  const handleToggle = async (connector) => {
    const next = !connector.enabled;
    setBusyId(connector.id);
    try {
      const updated = await setConnectorEnabled(connector.id, next);
      setConnectors((prev) =>
        prev.map((c) =>
          c.id === connector.id
            ? {
                ...c,
                ...updated,
                enabled: next,
                connectionStatus: updated.connectionStatus || (next ? "Connected" : "Disabled"),
              }
            : c
        )
      );
    } catch (err) {
      toast.error(getApiErrorMessage(err, `Could not ${next ? "enable" : "disable"} ${connector.name}`));
    } finally {
      setBusyId("");
    }
  };

  const handleSync = async (connector) => {
    const isRetry = connector.health === "Failed";
    setBusyId(connector.id);
    try {
      await startConnectorSync(connector.id);
      setConnectors((prev) =>
        prev.map((c) =>
          c.id === connector.id
            ? { ...c, connectionStatus: "Syncing", lastSync: Date.now() }
            : c
        )
      );
      setSyncsToday((n) => (typeof n === "number" ? n + 1 : 1));
      toast.success(isRetry ? `Retrying sync for ${connector.name}` : `Sync started for ${connector.name}`);
    } catch (err) {
      toast.error(getApiErrorMessage(err, `Could not start sync for ${connector.name}`));
    } finally {
      setBusyId("");
    }
  };

  if (loading) {
    return <p className="text-sm text-gray-500 px-1 py-10">Loading connectors…</p>;
  }

  if (error) {
    return (
      <div className="min-h-[220px] flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <p className="text-sm text-red-600">{error}</p>
          <button
            type="button"
            onClick={() => {
              setLoading(true);
              load()
                .catch((err) => setError(getApiErrorMessage(err, "Could not load connectors.")))
                .finally(() => setLoading(false));
            }}
            className="mt-4 h-10 px-4 rounded-lg bg-[#37B5A0] text-white text-sm font-medium"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (connectors.length === 0) {
    return (
      <div className="min-h-[420px] flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <h2 className="text-[20px] font-semibold text-slate-800">No connectors</h2>
          <p className="text-sm text-gray-500 mt-2">
            Connect external systems to start syncing data into Highwatch
          </p>
          <button
            type="button"
            onClick={openAdd}
            className="mt-5 inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-[#37B5A0] hover:bg-[#2FA090] text-white text-sm font-medium"
          >
            <LuPlus className="h-4 w-4" />
            Connect your first system
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
          <StatItem
            title="Total Connectors"
            value={stats.total}
            iconBg="#EEE3FE"
            icon={<LuUsers className="h-5 w-5 text-[#7C3AED]" />}
          />
          <StatItem
            title="Healthy"
            value={stats.healthy}
            valueClass="text-[#528416]"
            iconBg="#DDECC8"
            icon={<LuCheck className="h-5 w-5 text-[#528416]" />}
          />
          <StatItem
            title="Failed"
            value={stats.failed}
            valueClass="text-[#CD0F0F]"
            iconBg="#F9D7DC"
            icon={<LuX className="h-5 w-5 text-[#CD0F0F]" />}
          />
          <StatItem
            title="Syncs Today"
            value={stats.syncsToday}
            iconBg="#DEEFFB"
            icon={<LuClock className="h-5 w-5 text-[#0284C7]" />}
          />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full sm:w-[240px]">
          <LuSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search connectors..."
            className="w-full h-10 pl-9 pr-3 rounded-lg border border-[#E5E7EB] bg-white text-sm text-slate-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#37B5A0]/30"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-10 min-w-[120px] rounded-lg border border-[#E5E7EB] bg-white px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#37B5A0]/30"
        >
          <option value="">Status</option>
          {HEALTH_OPTIONS.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="h-10 min-w-[120px] rounded-lg border border-[#E5E7EB] bg-white px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#37B5A0]/30"
        >
          <option value="">Type</option>
          {Array.from(new Set(connectors.map((c) => c.type).filter(Boolean))).map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
        <div className="flex items-center gap-3 ml-auto">
          <button
            type="button"
            onClick={openAdd}
            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-[#37B5A0] hover:bg-[#2FA090] text-white text-sm font-medium"
          >
            <LuPlus className="h-4 w-4" />
            Add Connector
          </button>
          <button
            type="button"
            onClick={() => navigate("/settings/sync-history")}
            className="inline-flex items-center justify-center h-10 w-10 rounded-md border border-[#CDD0D7] bg-white text-slate-600 hover:bg-gray-50"
            aria-label="Sync history"
            title="Sync history"
          >
            <LuHistory className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="bg-white border border-[#E6E7EB] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px] text-sm">
            <thead>
              <tr className="bg-[#FAFBFD] text-left text-[12px] leading-[14px] font-medium text-[#898F99]">
                <th className="px-4 py-3">Connector</th>
                <th className="px-4 py-3">Health</th>
                <th className="px-4 py-3">Last Sync</th>
                <th className="px-4 py-3">Frequency</th>
                <th className="px-4 py-3">Enabled</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-sm text-gray-500">
                    No connectors match your filters.
                  </td>
                </tr>
              ) : (
                filtered.map((connector) => {
                  const failed = connector.health === "Failed";
                  return (
                    <tr
                      key={connector.id}
                      className="h-[76px] border-b border-[#E6E7EB] last:border-b-0 hover:bg-[#F6F7FA]"
                    >
                      <td className="px-4 py-3 align-middle">
                        <button
                          type="button"
                          onClick={() => openView(connector)}
                          className="flex items-center gap-3 min-w-0 text-left"
                        >
                          <ConnectorLogo type={connector.type} />
                          <div className="min-w-0">
                            <p className="text-[14px] leading-4 font-medium text-[#031F36] truncate">
                              {connector.name}
                            </p>
                            <p className="mt-0.5 text-[12px] leading-[14px] text-[#898F99]">
                              {connector.connectionStatus || "—"}
                            </p>
                          </div>
                        </button>
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <HealthBadge health={connector.health} />
                      </td>
                      <td className="px-4 py-3 align-middle text-[12px] leading-[14px] text-[#898F99] whitespace-nowrap">
                        {formatRelativeTime(connector.lastSync)}
                      </td>
                      <td className="px-4 py-3 align-middle text-[12px] leading-[14px] text-[#898F99]">
                        {connector.frequency || "—"}
                      </td>
                      <td className="px-4 align-middle">
                        <Toggle
                          checked={connector.enabled}
                          onChange={() => busyId !== connector.id && handleToggle(connector)}
                          label={`${connector.enabled ? "Disable" : "Enable"} ${connector.name}`}
                        />
                      </td>
                      <td className="px-4 align-middle">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => openView(connector)}
                            className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-[#CDD0D7] bg-white text-[#031F36] hover:bg-gray-50"
                            aria-label={`View ${connector.name}`}
                            title="View"
                          >
                            <LuEye className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => openEdit(connector)}
                            className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-[#CDD0D7] bg-white text-[#031F36] hover:bg-gray-50"
                            aria-label={`Edit ${connector.name}`}
                            title="Edit"
                          >
                            <LuPencil className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            disabled={busyId === connector.id}
                            onClick={() => handleSync(connector)}
                            className="h-8 px-3 rounded-md border border-[#CDD0D7] bg-white text-[12px] leading-[14px] text-[#031F36] whitespace-nowrap hover:bg-gray-50 disabled:opacity-50"
                          >
                            {failed ? "Retry Sync" : "Sync Now"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
